// src/pages/Finance.jsx
// Finance page: tabular detail view of every AnnualReportYear for the
// workspace with inline editable cells. Mirrors the broker's mental
// model of a spreadsheet — line items as rows, fiscal years as columns,
// click to edit, blur to save.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { API_BASE, getCsrfToken, request } from "../services/server";
import Main from "./Main";
import { makeCompanyNav } from "./nav";

// Editable fields, ordered the way a Swedish bokslut typically reads.
// Sections give the UI a visual grouping (Resultaträkning / Balansräkning / Övrigt).
const ROW_GROUPS = [
  {
    title: "Resultaträkning",
    rows: [
      { key: "revenue", label: "Nettoomsättning", kind: "currency" },
      { key: "result_after_financial_items", label: "Resultat efter finansiella poster", kind: "currency" },
      { key: "profit_for_year", label: "Årets resultat", kind: "currency" },
    ],
  },
  {
    title: "Balansräkning",
    rows: [
      { key: "total_assets", label: "Summa tillgångar", kind: "currency" },
      { key: "total_equity", label: "Eget kapital", kind: "currency" },
      { key: "total_equity_and_liabilities", label: "Summa eget kapital och skulder", kind: "currency" },
      { key: "cash_and_equivalents", label: "Kassa och bank", kind: "currency" },
      { key: "interest_bearing_short_term", label: "Räntebärande skulder (kort)", kind: "currency" },
      { key: "interest_bearing_long_term", label: "Räntebärande skulder (lång)", kind: "currency" },
      { key: "interest_bearing_total", label: "Räntebärande skulder (totalt)", kind: "currency" },
      { key: "net_cash", label: "Nettokassa", kind: "currency" },
    ],
  },
  {
    title: "Övrigt",
    rows: [
      { key: "employees", label: "Antal anställda", kind: "integer" },
      { key: "equity_ratio_percent", label: "Soliditet (%)", kind: "percent" },
    ],
  },
];

const ALL_EDITABLE_KEYS = ROW_GROUPS.flatMap((g) => g.rows.map((r) => r.key));

function formatCurrency(v) {
  if (v == null || v === "") return "";
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return new Intl.NumberFormat("sv-SE", { maximumFractionDigits: 0 }).format(Math.round(n));
}

function formatPercent(v) {
  if (v == null || v === "") return "";
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return `${n.toFixed(2).replace(/\.00$/, "")}`;
}

function parseInputForKind(raw, kind) {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;
  // Allow Swedish formatting: "1 234 567" or "1,234,567" or "1.234,5"
  const cleaned = s.replace(/\s/g, "").replace(/,/g, ".");
  if (kind === "percent") {
    const f = parseFloat(cleaned);
    return Number.isFinite(f) ? f : null;
  }
  // currency / integer — round to int, accept decimal input
  const f = parseFloat(cleaned);
  if (!Number.isFinite(f)) return null;
  return Math.round(f);
}

export default function FinancePage() {
  const { company: orgnr } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [savingCell, setSavingCell] = useState(null); // `${yearId}:${key}` while saving
  const [savedFlash, setSavedFlash] = useState(null);

  const load = useCallback(async () => {
    if (!orgnr) return;
    setLoading(true);
    setError("");
    try {
      const d = await request(`/company/finance/${encodeURIComponent(orgnr)}/`);
      setData(d);
    } catch (e) {
      setError(e?.body?.detail || e?.message || "Kunde inte ladda bokslut.");
    } finally {
      setLoading(false);
    }
  }, [orgnr]);

  useEffect(() => { load(); }, [load]);

  // Order years left→right by year_end descending — newest first.
  const years = useMemo(() => data?.years || [], [data]);
  const sieFiles = useMemo(() => data?.sie_files || [], [data]);

  function formatSiePeriod(sf) {
    // Prefer LTM dates since SIE files are usually loaded for rolling-12.
    const start = sf.ltm_start || sf.year_start;
    const end = sf.ltm_end || sf.year_end;
    if (start && end) return `${start} → ${end}`;
    if (end) return end;
    return sf.fiscal_year_label || "SIE";
  }

  async function saveCell(yearId, key, value) {
    const cellId = `${yearId}:${key}`;
    setSavingCell(cellId);
    try {
      const csrf = getCsrfToken();
      const res = await fetch(`${API_BASE}/company/finance/${encodeURIComponent(orgnr)}/${yearId}/`, {
        method: "PATCH",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(csrf ? { "X-CSRFToken": csrf } : {}),
        },
        body: JSON.stringify({ [key]: value }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      const updated = await res.json();
      // Replace the row in local state.
      setData((prev) => ({
        ...prev,
        years: (prev?.years || []).map((y) => (y.id === yearId ? updated : y)),
      }));
      setSavedFlash(cellId);
      setTimeout(() => setSavedFlash((cur) => (cur === cellId ? null : cur)), 1200);
    } catch (e) {
      alert(`Kunde inte spara: ${e.message}`);
    } finally {
      setSavingCell((cur) => (cur === cellId ? null : cur));
    }
  }

  return (
    <Main navItems={makeCompanyNav(orgnr)}>
      <div style={{ padding: "24px 32px", maxWidth: 1400, margin: "0 auto" }}>
        <header style={{ marginBottom: 24 }}>
          <h1 style={{ fontSize: 26, fontWeight: 700, color: "#1C1917", marginBottom: 4 }}>
            Finans
          </h1>
          <p style={{ fontSize: 14, color: "#78716C", margin: 0 }}>
            {data?.company_name ? `${data.company_name} — ` : ""}
            Detaljerad vy av alla bokslut. Klicka i en cell för att justera siffran — sparas automatiskt på blur.
          </p>
        </header>

        {loading && <div style={{ color: "#78716C" }}>Laddar…</div>}
        {error && (
          <div style={{ padding: 12, background: "#FEE2E2", border: "1px solid #FECACA", color: "#991B1B", borderRadius: 8 }}>
            {error}
          </div>
        )}

        {!loading && !error && years.length === 0 && (
          <div style={{ padding: 16, background: "#FEF3C7", border: "1px solid #FDE68A", color: "#92400E", borderRadius: 8 }}>
            Inga bokslut hittades för det här bolaget än. Kör "Hämta data" på Dashboard.
          </div>
        )}

        {!loading && !error && years.length > 0 && (
          <div style={{ overflowX: "auto", border: "1px solid #E7E5E4", borderRadius: 10, background: "#fff" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#FAFAF9", borderBottom: "1px solid #E7E5E4" }}>
                  <th style={{ textAlign: "left", padding: "12px 14px", fontWeight: 600, color: "#1C1917", minWidth: 240 }}>
                    Post
                  </th>
                  {years.map((y) => (
                    <th key={y.id} style={{ textAlign: "right", padding: "12px 14px", fontWeight: 600, color: "#1C1917", minWidth: 140 }}>
                      <div>{y.fiscal_year_label}</div>
                      <div style={{ fontSize: 11, fontWeight: 400, color: "#A8A29E", marginTop: 2 }}>
                        {y.source === "llm" ? "OCR + AI" : y.source === "manual-input" ? "Manuell" : "Auto"}
                      </div>
                      {y.has_pdf && (
                        <a
                          href={`${API_BASE}/company/finance/${encodeURIComponent(orgnr)}/${y.id}/pdf/`}
                          target="_blank"
                          rel="noreferrer"
                          title="Ladda ned bokslut (PDF)"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            marginTop: 6,
                            padding: "3px 8px",
                            fontSize: 11,
                            fontWeight: 500,
                            color: "#1C1917",
                            background: "#F5F5F4",
                            border: "1px solid #E7E5E4",
                            borderRadius: 6,
                            textDecoration: "none",
                          }}
                        >
                          ↓ PDF
                        </a>
                      )}
                    </th>
                  ))}
                  {sieFiles.map((sf) => (
                    <th
                      key={`sie-${sf.id}`}
                      style={{
                        textAlign: "right",
                        padding: "12px 14px",
                        fontWeight: 600,
                        color: "#1C1917",
                        minWidth: 160,
                        background: "#F0FDF4",
                        borderLeft: "1px solid #BBF7D0",
                      }}
                    >
                      <div>SIE</div>
                      <div style={{ fontSize: 11, fontWeight: 400, color: "#15803D", marginTop: 2 }}>
                        {formatSiePeriod(sf)}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ROW_GROUPS.map((group, gi) => (
                  <React.Fragment key={group.title}>
                    <tr>
                      <td
                        colSpan={1 + years.length + sieFiles.length}
                        style={{
                          padding: "14px 14px 6px",
                          fontSize: 11,
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          color: "#57534E",
                          background: gi === 0 ? "transparent" : "#FAFAF9",
                          borderTop: gi === 0 ? "none" : "1px solid #E7E5E4",
                        }}
                      >
                        {group.title}
                      </td>
                    </tr>
                    {group.rows.map((row) => (
                      <tr key={row.key} style={{ borderBottom: "1px solid #F5F5F4" }}>
                        <td style={{ padding: "10px 14px", color: "#44403C" }}>{row.label}</td>
                        {years.map((y) => {
                          const value = y[row.key];
                          const cellId = `${y.id}:${row.key}`;
                          const isSaving = savingCell === cellId;
                          const isFlash = savedFlash === cellId;
                          const display = row.kind === "percent" ? formatPercent(value) : formatCurrency(value);
                          return (
                            <td
                              key={y.id}
                              style={{
                                padding: 0,
                                textAlign: "right",
                                background: isFlash ? "#DCFCE7" : "transparent",
                                transition: "background 0.4s",
                                position: "relative",
                              }}
                            >
                              <input
                                type="text"
                                inputMode={row.kind === "percent" ? "decimal" : "numeric"}
                                defaultValue={display}
                                disabled={isSaving}
                                onBlur={(e) => {
                                  const next = parseInputForKind(e.target.value, row.kind);
                                  const prev = value;
                                  // Avoid no-op save when value unchanged.
                                  if (next === prev || (next == null && (prev == null || prev === ""))) return;
                                  saveCell(y.id, row.key, next);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") e.target.blur();
                                  if (e.key === "Escape") {
                                    e.target.value = display;
                                    e.target.blur();
                                  }
                                }}
                                style={{
                                  width: "100%",
                                  textAlign: "right",
                                  padding: "10px 14px",
                                  border: "none",
                                  background: "transparent",
                                  fontFamily: "inherit",
                                  fontSize: 13,
                                  color: value == null ? "#A8A29E" : "#1C1917",
                                  outline: "none",
                                }}
                              />
                              {isSaving && (
                                <span style={{
                                  position: "absolute", right: 6, top: "50%",
                                  transform: "translateY(-50%)", fontSize: 10, color: "#78716C",
                                }}>…</span>
                              )}
                            </td>
                          );
                        })}
                        {sieFiles.map((sf) => {
                          // SIE only carries revenue (full period + LTM).
                          // Other rows render as "—" so the column stays
                          // visible without misleading zeros.
                          let val = null;
                          if (row.key === "revenue") {
                            val = sf.ltm_total_revenue ?? sf.total_revenue;
                          }
                          const display = val == null ? "—" : formatCurrency(val);
                          return (
                            <td
                              key={`sie-${sf.id}`}
                              style={{
                                padding: "10px 14px",
                                textAlign: "right",
                                color: val == null ? "#A8A29E" : "#166534",
                                background: "#F0FDF4",
                                borderLeft: "1px solid #BBF7D0",
                              }}
                            >
                              {display}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && !error && years.length > 0 && (
          <p style={{ marginTop: 16, fontSize: 12, color: "#A8A29E" }}>
            Manuella ändringar märks i raw_extracted så de inte skrivs över vid framtida data-synker.
            Tom cell = värdet rensas.
          </p>
        )}
      </div>
    </Main>
  );
}
